import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { SearchResult } from '../_models/SearchResult';
import { MagazzinoLocale } from '../_models/MagazzinoLocale';
import { MagazzinoAreaLocale } from '../_models/MagazzinoAreaLocale';
import { VoceGiacenza } from '../_models/VoceGiacenza';

// Anagrafica locale di magazzini e aree (feature 118, issue #3354).
@Injectable({ providedIn: 'root' })
export class MagazziniService {
  constructor(private http: HttpClient) {}

  // ── Pagina di gestione: restituisce TUTTO, comprese le voci scadute o di terzisti ──────────

  async getMagazzini(orgId: number) {
    return firstValueFrom(this.http
      .get<SearchResult<MagazzinoLocale>>(`${environment.localApiUrl}api/Magazzini/${orgId}`));
  }

  async getMagazziniFiltered(orgId: number, search = '', page = 0, count = 0,
                             sortField = 'codice', sortAsc = true, excludeInactive = false,
                             includeAree = false) {
    return firstValueFrom(this.http
      .post<SearchResult<MagazzinoLocale>>(`${environment.localApiUrl}api/Magazzini/Filtered/${orgId}`,
        {search, page, count, sortField, sortAsc, excludeInactive, includeAree}));
  }

  async createMagazzino(magazzino: MagazzinoLocale) {
    return firstValueFrom(this.http
      .post<MagazzinoLocale>(`${environment.localApiUrl}api/Magazzini/Create`, magazzino));
  }

  async updateMagazzino(magazzino: MagazzinoLocale) {
    return firstValueFrom(this.http
      .post<MagazzinoLocale>(`${environment.localApiUrl}api/Magazzini/Update`, magazzino));
  }

  async deleteMagazzino(orgId: number, code: string) {
    return firstValueFrom(this.http
      .get<string>(`${environment.localApiUrl}api/Magazzini/Delete/${orgId}/${code}`));
  }

  // ── Aree ──────────────────────────────────────────────────────────────────────────────────

  async getAree(orgId: number, codMag: string) {
    return firstValueFrom(this.http
      .get<SearchResult<MagazzinoAreaLocale>>(`${environment.localApiUrl}api/Magazzini/Aree/${orgId}/${codMag}`));
  }

  async createArea(area: MagazzinoAreaLocale) {
    return firstValueFrom(this.http
      .post<MagazzinoAreaLocale>(`${environment.localApiUrl}api/Magazzini/AreaCreate`, area));
  }

  // E' anche l'endpoint del toggle "Mostra in Lista Spedizioni" e del riordino.
  async updateArea(area: MagazzinoAreaLocale) {
    return firstValueFrom(this.http
      .post<MagazzinoAreaLocale>(`${environment.localApiUrl}api/Magazzini/AreaUpdate`, area));
  }

  async deleteArea(orgId: number, codMag: string, code: string) {
    return firstValueFrom(this.http
      .get<string>(`${environment.localApiUrl}api/Magazzini/AreaDelete/${orgId}/${codMag}/${code}`));
  }

  // ── Import da ESOLVER ─────────────────────────────────────────────────────────────────────

  // Al netto di quelli gia importati (FR-005).
  async getMagazziniEsolver(orgId: number) {
    return firstValueFrom(this.http
      .get<SearchResult<MagazzinoLocale>>(`${environment.localApiUrl}api/Magazzini/Esolver/${orgId}`));
  }

  // Elenco completo: le voci con id > 0 sono gia in anagrafica e l'import le AGGIORNA (FR-035).
  async getMagazziniEsolverAggiornabili(orgId: number) {
    return firstValueFrom(this.http
      .get<SearchResult<MagazzinoLocale>>(`${environment.localApiUrl}api/Magazzini/EsolverAggiornabili/${orgId}`));
  }

  async importMagazzini(orgId: number, magazzini: MagazzinoLocale[]) {
    return firstValueFrom(this.http
      .post<SearchResult<MagazzinoLocale>>(`${environment.localApiUrl}api/Magazzini/Import/${orgId}`, magazzini));
  }

  async getAreeEsolver(orgId: number, codMag: string) {
    return firstValueFrom(this.http
      .get<SearchResult<MagazzinoAreaLocale>>(
        `${environment.localApiUrl}api/Magazzini/AreeEsolver/${orgId}/${codMag}`));
  }

  async getAreeEsolverAggiornabili(orgId: number, codMag: string) {
    return firstValueFrom(this.http
      .get<SearchResult<MagazzinoAreaLocale>>(
        `${environment.localApiUrl}api/Magazzini/AreeEsolverAggiornabili/${orgId}/${codMag}`));
  }

  async importAree(orgId: number, codMag: string, aree: MagazzinoAreaLocale[]) {
    return firstValueFrom(this.http
      .post<SearchResult<MagazzinoAreaLocale>>(
        `${environment.localApiUrl}api/Magazzini/AreeImport/${orgId}/${codMag}`, aree));
  }

  // ── Selettore delle funzioni operative (US5) ───────────────────────────────────────────────

  // NON riusare getMagazzini: quello alimenta la pagina di gestione e restituisce anche le voci
  // scadute e quelle di terzisti. Qui il backend riproduce i filtri del gestionale (FR-036).
  async getMagazziniSelezionabili(orgId: number) {
    return firstValueFrom(this.http
      .get<SearchResult<MagazzinoLocale>>(`${environment.localApiUrl}api/Magazzini/Selezionabili/${orgId}`));
  }

  // ── Colonne giacenza della Lista Spedizioni ───────────────────────────────────────────────

  async getVociGiacenza(orgId: number) {
    return firstValueFrom(this.http
      .get<SearchResult<VoceGiacenza>>(`${environment.localApiUrl}api/Magazzini/VociGiacenza/${orgId}`));
  }
}
